/**
 * ================================================================================
 * Match It Up - Professional Networking Platform
 * Copyright © 2026 Match It Up Technologies Pvt Ltd. All rights reserved.
 *
 * Unauthorized copying, modification, distribution, or use of this software
 * is prohibited. This software is proprietary and confidential.
 * ================================================================================
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';

// Error boundary for admin-only lazy routes — prevents chunk load errors from
// falling through to the generic 404 NotFound page
class AdminRouteErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return <Navigate to={this.props.fallback || '/dashboard'} replace />;
    }
    return this.props.children;
  }
}
import { usePageTracking } from './hooks/usePageTracking';
import CookieConsent from './components/CookieConsent';
import AuthCallback from './components/AuthCallback';
import ReportBugButton from './components/ReportBugButton';
import HelpWidget from './components/HelpWidget';
import './App.css';

// ============================================
// LAZY LOADED PAGES - Code Splitting
// ============================================
// Only Landing, Login, Register load immediately (critical path)
// All other pages load on-demand when user navigates

// Critical pages - load immediately (no lazy loading = no white flash)
import Landing from './pages/Landing';
import LandingV2 from './pages/LandingV2';
import Login from './pages/Login';
import Register from './pages/Register';
import SocialDone from './pages/SocialDone';
import FAQ from './pages/FAQ';
import Pricing from './pages/Pricing';
import DiscoverPage from './pages/DiscoverPage';
import About from './pages/About';
import Resources from './pages/Resources';

// Lazy loaded pages - load on demand (for less frequently accessed pages)
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ProfileSetupSample = lazy(() => import('./pages/ProfileSetupSample'));
const Dashboard = lazy(() => import('./pages/Dashboard/DashboardNew'));
const Browse = lazy(() => import('./pages/Browse'));
const Matches = lazy(() => import('./pages/Matches'));
const ProfileEdit = lazy(() => import('./pages/ProfileEdit'));
const Messages = lazy(() => import('./pages/Messages'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminEmailDebug = lazy(() => import('./pages/AdminEmailDebug'));
const FundPlanner = lazy(() => import('./pages/FundPlanner'));
const FounderInsights = lazy(() => import('./pages/FounderInsights'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const WhoLikedYou = lazy(() => import('./pages/WhoLikedYou'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const FoundersNote = lazy(() => import('./pages/FoundersNote'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const JoinCommunity = lazy(() => import('./pages/JoinCommunity'));
const CommunityAdminPortal = lazy(() => import('./pages/CommunityAdminPortal'));
const HelpCommunities = lazy(() => import('./pages/HelpCommunities'));
const HelpMuPin = lazy(() => import('./pages/HelpMuPin'));
const HelpBrewCircles = lazy(() => import('./pages/HelpBrewCircles'));
const HelpAntiNetworking = lazy(() => import('./pages/HelpAntiNetworking'));
const HelpRelationshipDecay = lazy(() => import('./pages/HelpRelationshipDecay'));
const HelpPayItForward = lazy(() => import('./pages/HelpPayItForward'));
const BrewCircles = lazy(() => import('./pages/BrewCircles'));
const GettingStarted = lazy(() => import('./pages/GettingStarted'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ServerError = lazy(() => import('./pages/ServerError'));
const Partners = lazy(() => import('./pages/Partners'));
const PartnerCafes = lazy(() => import('./pages/PartnerCafes'));
const PartnerAlumni = lazy(() => import('./pages/PartnerAlumni'));
const PartnerCoworking = lazy(() => import('./pages/PartnerCoworking'));
const WhatMatchItUpIsNot = lazy(() => import('./pages/WhatMatchItUpIsNot'));
const NotADatingSite = lazy(() => import('./pages/NotADatingSite'));
const MuPinLookup = lazy(() => import('./pages/MuPinLookup'));
const Article = lazy(() => import('./pages/Article'));
const PitchDeck = lazy(() => import('./pages/PitchDeck'));
const NetworkBot = lazy(() => import('./pages/NetworkBot'));
const AgentProfile = lazy(() => import('./pages/AgentProfile'));
const AgentPostDetail = lazy(() => import('./pages/AgentPostDetail'));
const DeveloperDocs = lazy(() => import('./pages/DeveloperDocs'));
const DocsPage      = lazy(() => import('./pages/DocsPage'));
const ProtocolRooms = lazy(() => import('./pages/ProtocolRooms'));
const Marketplace       = lazy(() => import('./pages/Marketplace'));
const MarketplaceDetail = lazy(() => import('./pages/MarketplaceDetail'));
const ContractView      = lazy(() => import('./pages/ContractView'));
const DisputeAdmin      = lazy(() => import('./pages/DisputeAdmin'));
const ProtocolRoom = lazy(() => import('./pages/ProtocolRoom'));
const OneAgentPolicy = lazy(() => import('./pages/OneAgentPolicy'));
const Help = lazy(() => import('./pages/Help'));
const ClaimAgent = lazy(() => import('./pages/ClaimAgent'));
const AgentCredits = lazy(() => import('./pages/AgentCredits'));
const NDAPage = lazy(() => import('./pages/NDAPage'));
const Demo = lazy(() => import('./pages/Demo'));
const LandingDemo = lazy(() => import('./pages/LandingDemo'));
const LandingDemo2 = lazy(() => import('./pages/LandingDemo2'));
const LandingDemo3 = lazy(() => import('./pages/LandingDemo3'));
// Match Hub — FEATURE FLAG: Admin-only, not linked in navigation
const MIUEvents = lazy(() => import('./pages/Mixer'));
const MyAgentPage = lazy(() => import('./pages/MyAgentPage'));
const AIPage = lazy(() => import('./pages/AIPage'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const Resubscribe = lazy(() => import('./pages/Resubscribe'));

// SEO Landing Pages (hidden from nav, indexed by search engines)
const OnlineProfessionalNetworking = lazy(() => import('./pages/seo/OnlineProfessionalNetworking'));
const NetworkingMumbai = lazy(() => import('./pages/seo/NetworkingMumbai'));
const NetworkingBangalore = lazy(() => import('./pages/seo/NetworkingBangalore'));
const NetworkingDelhiNCR = lazy(() => import('./pages/seo/NetworkingDelhiNCR'));
const NetworkingGurgaon = lazy(() => import('./pages/seo/NetworkingGurgaon'));
const ReferralNetworkingGuide = lazy(() => import('./pages/seo/ReferralNetworkingGuide'));

// Static Article Pages (SEO Phase 2 - fully hardcoded JSX for AI crawler visibility)
const ArticleTrustScore = lazy(() => import('./pages/articles/ArticleTrustScore'));
const ArticleLinkedIn = lazy(() => import('./pages/articles/ArticleLinkedIn'));
const ArticleVerify = lazy(() => import('./pages/articles/ArticleVerify'));
const ArticleNetworkDying = lazy(() => import('./pages/articles/ArticleNetworkDying'));
const ArticleBestAI = lazy(() => import('./pages/articles/ArticleBestAI'));
const ArticleTrustInIndia = lazy(() => import('./pages/articles/ArticleTrustInIndia'));
const ArticleNetworkingBroken = lazy(() => import('./pages/articles/ArticleNetworkingBroken'));
const ArticleDatingApp = lazy(() => import('./pages/articles/ArticleDatingApp'));
const ArticleNetworkBot = lazy(() => import('./pages/articles/ArticleNetworkBot'));
const ArticleWarmIntros = lazy(() => import('./pages/articles/ArticleWarmIntros'));
const ArticleCoFounder = lazy(() => import('./pages/articles/ArticleCoFounder'));
const ArticleNetworkBotProtocol = lazy(() => import('./pages/articles/ArticleNetworkBotProtocol'));
const ShortLinkRedirect = lazy(() => import('./pages/ShortLinkRedirect'));

// Loading fallback component for lazy loaded pages
// Using minimal/invisible fallback to prevent flicker during navigation
const PageLoader = () => (
  <div className="min-h-screen bg-white" />
);

// Scroll to top on route change - handles scroll AFTER navigation completes
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Page tracking wrapper component
const PageTracker = ({ children }) => {
  usePageTracking();
  return children;
};

const LegacyProtocolRoomsRedirect = () => (
  <ProtocolRooms />
);

const LegacyProtocolRoomRedirect = () => {
  const { slug } = useParams();
  const safeSlug = slug ? encodeURIComponent(slug) : '';
  return <Navigate to={safeSlug ? `/networkbot/network?room=${safeSlug}` : '/networkbot/network'} replace />;
};

const normalizeRedirectTarget = (redirectTo) => {
  if (!redirectTo) return null;

  const cleaned = String(redirectTo).trim().replace(/^\/+/, '');
  if (!cleaned) return null;
  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) return null;
  if (cleaned.startsWith('social-done')) return 'dashboard';
  if (cleaned === 'profile-setup') return 'profile-edit';

  return cleaned;
};

const ProtectedRoute = ({ children, requiresCompleteProfile = true }) => {
  const { user, loading, initialLoadComplete } = useAuth();
  const location = useLocation();

  // Show loader only during initial auth check, not on subsequent navigations
  if (loading && !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-rose-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const from = location.pathname.slice(1); // strip leading /
    return <Navigate to={from ? `/login?redirect=${encodeURIComponent(from)}` : '/login'} replace />;
  }

  if (requiresCompleteProfile) {
    // Trust profile_activated flag first, then check gives/asks
    // Also check if profile data is still being populated (gives/asks might load async)
    const hasCompletedProfile = user.profile_activated === true || 
      (Array.isArray(user.gives) && user.gives.length > 0 && Array.isArray(user.asks) && user.asks.length > 0);
    
    // Don't redirect if profile_activated is explicitly true
    if (!hasCompletedProfile && user.profile_activated !== true) {
      return <Navigate to="/profile-edit" replace />;
    }
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, initialLoadComplete } = useAuth();
  const location = useLocation();

  // Show loader only during initial auth check
  if (loading && !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-rose-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // Check for redirect parameter or pending subscription
    // Use React Router's location.search for reliable access
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = normalizeRedirectTarget(searchParams.get('redirect'));
    const pendingSubscription = localStorage.getItem('pendingSubscription');
    
    if (redirectTo) {
      return <Navigate to={`/${redirectTo}`} replace />;
    }
    
    if (pendingSubscription) {
      return <Navigate to="/pricing" replace />;
    }
    
    // Always go to dashboard — Browse owns the profile-completion gate now.
    const hasCompletedProfile = user.profile_activated || (user.gives && user.gives.length > 0 && user.asks && user.asks.length > 0);
    return <Navigate to={hasCompletedProfile ? "/dashboard" : "/dashboard"} replace />;
  }

  return children;
};

// AppRouter component that checks for session_id synchronously
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions - must happen BEFORE ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<LandingV2 />} />
      <Route path="/about" element={<About />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/profile-setup-sample" element={<ProfileSetupSample />} />
      <Route
        path="/profile-setup"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <Navigate to="/profile-edit" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/browse"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <Browse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <Matches />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <ProfileEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile-edit"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <ProfileEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:matchId?"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/who-liked-you"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <WhoLikedYou />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brew-circles"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <BrewCircles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/getting-started"
        element={
          <ProtectedRoute>
            <GettingStarted />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/email-debug" element={<AdminEmailDebug />} />
      <Route path="/admin/fund-planner" element={<FundPlanner />} />
      <Route path="/founder-insights" element={<FounderInsights />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment-return" element={<PaymentReturn />} />
      <Route path="/founders-note" element={<FoundersNote />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/community-guidelines" element={<CommunityGuidelines />} />
      <Route path="/policy/one-agent-per-human" element={<OneAgentPolicy />} />
      <Route path="/help/communities" element={<HelpCommunities />} />
      <Route path="/help/mu-pin" element={<HelpMuPin />} />
      <Route path="/help/brew-circles" element={<HelpBrewCircles />} />
      <Route path="/help/anti-networking" element={<HelpAntiNetworking />} />
      <Route path="/help/relationship-decay" element={<HelpRelationshipDecay />} />
      <Route path="/help/pay-it-forward" element={<HelpPayItForward />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/partners/cafes" element={<PartnerCafes />} />
      <Route path="/partners/alumni" element={<PartnerAlumni />} />
      <Route path="/partners/coworking" element={<PartnerCoworking />} />
      <Route path="/what-match-it-up-is-not" element={<WhatMatchItUpIsNot />} />
      <Route path="/not-a-dating-site" element={<NotADatingSite />} />
      <Route path="/pitch-deck" element={<PitchDeck />} />
      <Route path="/investor" element={<PitchDeck />} />
      <Route path="/discover" element={<DiscoverPage />} />
      <Route path="/resources" element={<Resources />} />
      {/* Static article routes (SEO Phase 2) - must come BEFORE the generic :slug fallback */}
      <Route path="/resources/why-we-will-never-show-you-your-trust-score" element={<ArticleTrustScore />} />
      <Route path="/resources/linkedin-tells-you-who-matchitup-tells-you-why" element={<ArticleLinkedIn />} />
      <Route path="/resources/we-verify-professionals-without-asking-for-your-passwords" element={<ArticleVerify />} />
      <Route path="/resources/why-your-professional-network-is-quietly-dying" element={<ArticleNetworkDying />} />
      <Route path="/resources/the-best-ai-is-the-kind-you-never-notice" element={<ArticleBestAI />} />
      <Route path="/resources/why-professional-trust-works-differently-in-india" element={<ArticleTrustInIndia />} />
      <Route path="/resources/networking-is-broken-we-decided-to-do-something-about-it" element={<ArticleNetworkingBroken />} />
      <Route path="/resources/indias-first-business-platform-that-looks-like-a-dating-app" element={<ArticleDatingApp />} />
      <Route path="/resources/your-next-business-partnership-should-not-require-9-hours-of-your-week" element={<ArticleNetworkBot />} />
      <Route path="/resources/how-to-get-warm-introductions-india" element={<ArticleWarmIntros />} />
      <Route path="/resources/find-co-founder-india-2026" element={<ArticleCoFounder />} />
      <Route path="/resources/networkbot-protocol-ai-agent-networking-api" element={<ArticleNetworkBotProtocol />} />
      <Route path="/resources/:slug" element={<Article />} />
      <Route path="/profile/:miPin" element={<PublicProfile />} />
      <Route path="/m/:miPin" element={<PublicProfile />} />
      <Route path="/lookup/:pin" element={<MuPinLookup />} />
      <Route path="/lookup" element={<MuPinLookup />} />
      <Route path="/join/:code" element={<JoinCommunity />} />
      <Route path="/join" element={<JoinCommunity />} />
      <Route path="/join-community" element={<JoinCommunity />} />
      <Route path="/community-admin/:communityId" element={<CommunityAdminPortal />} />
      <Route path="/community-admin" element={<CommunityAdminPortal />} />
      <Route path="/networkbot/:tab?" element={<NetworkBot />} />
      <Route path="/bot/:agent_id" element={<AgentProfile />} />
      <Route path="/post/:post_id" element={<AgentPostDetail />} />
      <Route path="/developer-docs" element={<DeveloperDocs />} />
      <Route path="/docs"           element={<DocsPage />} />
      <Route path="/protocol-rooms" element={<LegacyProtocolRoomsRedirect />} />
      <Route path="/protocol-rooms/:slug" element={<LegacyProtocolRoomRedirect />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/:intentId" element={<MarketplaceDetail />} />
      <Route path="/contracts/:contractId" element={<ContractView />} />
      <Route path="/admin/disputes" element={<DisputeAdmin />} />
      <Route path="/help" element={<Help />} />
      <Route path="/claim-agent" element={<ClaimAgent />} />
      <Route path="/claim-agent/verify" element={<ClaimAgent />} />
      <Route path="/agent-credits" element={<AgentCredits />} />
      <Route path="/p/:code" element={<ShortLinkRedirect />} />
      {/* MIU Events Coming Soon — public, no login required */}
      <Route path="/miuevents" element={<MIUEvents />} />
      <Route path="/mixer" element={<Navigate to="/miuevents" replace />} />
      {/* My Agent — dedicated agent settings page */}
      <Route
        path="/my-agent"
        element={
          <ProtectedRoute requiresCompleteProfile={false}>
            <MyAgentPage />
          </ProtectedRoute>
        }
      />
      <Route path="/ai" element={<Navigate to="/nobs" replace />} />
      <Route path="/try" element={<Navigate to="/nobs" replace />} />
      <Route path="/nobs" element={<AIPage />} />
      <Route path="/nda" element={<NDAPage />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/landing-demo" element={<LandingDemo />} />
      <Route path="/landing-demo-2" element={<LandingDemo2 />} />
      <Route path="/landing-demo-3" element={<LandingDemo3 />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/resubscribe" element={<Resubscribe />} />
      
      {/* SEO Landing Pages - Not linked in nav, indexed by search engines */}
      <Route path="/networking/online-professional-networking" element={<OnlineProfessionalNetworking />} />
      <Route path="/networking/mumbai" element={<NetworkingMumbai />} />
      <Route path="/networking/bangalore" element={<NetworkingBangalore />} />
      <Route path="/networking/delhi-ncr" element={<NetworkingDelhiNCR />} />
      <Route path="/networking/gurgaon" element={<NetworkingGurgaon />} />
      <Route path="/guides/referral-networking" element={<ReferralNetworkingGuide />} />
      
      <Route path="/500" element={<ServerError />} />
      <Route path="/social-done" element={<SocialDone />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <HelmetProvider>
      {/* Global schema.org: WebSite + Organization */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Match It Up",
          "url": "https://matchitup.in",
          "description": "India's AI-powered professional networking platform. Match by what you give and what you need.",
          "potentialAction": { "@type": "SearchAction", "target": "https://matchitup.in/discover?q={search_term_string}", "query-input": "required name=search_term_string" }
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Match It Up",
          "url": "https://matchitup.in",
          "logo": "https://matchitup.in/logo512.png",
          "sameAs": ["https://twitter.com/matchitup_in", "https://linkedin.com/company/matchitup"],
          "description": "AI-powered business networking platform connecting professionals based on mutual value exchange.",
          "contactPoint": { "@type": "ContactPoint", "contactType": "customer support", "url": "https://matchitup.in/contact" }
        }
      ]) }} />
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker>
          <Toaster position="top-center" richColors />
          <CookieConsent />
          <ReportBugButton />
          <HelpWidget />
          <AppRouter />
        </PageTracker>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
